import { useState, useCallback } from 'react'
import { useStore } from './hooks/useStore.js'
import { useShoppingList } from './hooks/useShoppingList.js'
import { useRoute } from './hooks/useRoute.js'
import Header from './components/Header.jsx'
import StoreList from './components/StoreList.jsx'
import GridEditor from './components/GridEditor.jsx'
import ShoppingList from './components/ShoppingList.jsx'
import RouteView from './components/RouteView.jsx'
import NavigationMode from './components/NavigationMode.jsx'

export default function App() {
  // Simple hash-based routing
  const [view, setView] = useState('stores') // 'stores' | 'editor' | 'list'
  const [activeStoreId, setActiveStoreId] = useState(null)
  const [showNavigation, setShowNavigation] = useState(false)

  const {
    stores,
    store,
    grid,
    zones,
    loading: storeLoading,
    saving,
    createStore,
    deleteStore,
    paintCell,
    paintCells,
    undo,
    redo,
    saveZones,
    forceSave,
  } = useStore(activeStoreId)

  const {
    activeList,
    loading: listLoading,
    createList,
    addItem,
    removeItem,
    toggleItem,
    updateItemCategory,
  } = useShoppingList(activeStoreId)

  const {
    route,
    currentStep,
    calculating,
    computeRoute,
    nextStep,
    prevStep,
    clearRoute,
    isLastStep,
    progress,
  } = useRoute()

  // Navigation helpers
  const navigateToStore = useCallback((storeId, targetView = 'editor') => {
    setActiveStoreId(storeId)
    setView(targetView)
  }, [])

  const navigateToStores = useCallback(() => {
    forceSave?.()
    setActiveStoreId(null)
    setView('stores')
    clearRoute()
  }, [forceSave, clearRoute])

  const navigateToList = useCallback(() => {
    setView('list')
  }, [])

  const handleComputeRoute = useCallback(() => {
    if (!grid || !activeList) return
    computeRoute(grid, zones, activeList.items)
  }, [grid, zones, activeList, computeRoute])

  const handleStartNavigation = useCallback(() => {
    setShowNavigation(true)
  }, [])

  const handleCloseNavigation = useCallback(() => {
    setShowNavigation(false)
  }, [])

  return (
    <>
      <Header
        storeName={store?.name}
        view={view}
        onNavigate={(target) => {
          if (target === 'stores') navigateToStores()
        }}
        saving={activeStoreId ? saving : undefined}
      />

      <main className="main">
        {view === 'stores' && (
          <StoreList
            stores={stores}
            loading={storeLoading}
            onCreate={createStore}
            onDelete={deleteStore}
            onSelect={navigateToStore}
          />
        )}

        {view === 'editor' && activeStoreId && (
          <GridEditor
            grid={grid}
            zones={zones}
            onPaintCell={paintCell}
            onPaintCells={paintCells}
            onUndo={undo}
            onRedo={redo}
            onSaveZones={saveZones}
            onNavigateToList={navigateToList}
          />
        )}

        {view === 'list' && activeStoreId && (
          <div className="shopping-view">
            <ShoppingList
              activeList={activeList}
              onAddItem={addItem}
              onRemoveItem={removeItem}
              onToggleItem={toggleItem}
              onUpdateItemCategory={updateItemCategory}
              onCreateList={createList}
              onComputeRoute={handleComputeRoute}
              calculating={calculating}
            />

            {route && (
              <RouteView
                grid={grid}
                zones={zones}
                route={route}
                onStartNavigation={handleStartNavigation}
              />
            )}
          </div>
        )}
      </main>

      {/* Full-screen navigation overlay */}
      {showNavigation && route && (
        <NavigationMode
          route={route}
          currentStep={currentStep}
          onNext={nextStep}
          onPrev={prevStep}
          onClose={handleCloseNavigation}
          isLastStep={isLastStep}
          progress={progress}
        />
      )}

      <footer className="footer">
        <p>Winkelroute — Navigeer slim door je supermarkt 🛒</p>
      </footer>
    </>
  )
}
